import withProgress from 'core/components/progress/withProgress'
import Dropdown from 'core/elements/dropdown'
import useListAction from 'core/hooks/useListAction'
import { listNetworks } from 'openstack/components/networks/actions'
import {
  networksByIdSelector,
  subnetsFromAllNetworksSelector,
} from 'openstack/components/networks/selectors'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

function buildSubnetOption(subnet, network, includeNetworkName) {
  const networkPrefix = includeNetworkName ? `${network?.name || network?.id}: ` : ''
  const subnetLabel = `${networkPrefix}${subnet?.cidr} (${subnet?.name || subnet?.id})`
  const portAndIpsAvailable = network?.availablePortAndIPs > 0
  return {
    label: subnetLabel,
    value: subnet,
    disabled: !portAndIpsAvailable,
    tooltip: !portAndIpsAvailable ? 'Network has no available IP addresses or ports' : null,
  }
}

export default function SubnetPicklist({
  disabled = false,
  name = 'subnet',
  label = 'Subnet',
  networkId = null,
  includeNoneOption = false,
  includeNetworkName = true,
  includeAutomaticOption = false,
  ...rest
}) {
  const { loading } = useListAction(listNetworks)
  const networksById = useSelector(networksByIdSelector)
  const subnets = useSelector(subnetsFromAllNetworksSelector)

  const networkSubnets = useMemo(() => {
    if (!networkId) {
      return subnets
    }
    return networksById[networkId]?.subnetDetails || []
  }, [networkId, subnets])

  const options = useMemo(() => {
    // will need to filter out taken private IPs
    const subnetOptions = networkSubnets.map((subnet) => {
      const network = networksById[subnet.network_id]
      return buildSubnetOption(subnet, network, includeNetworkName)
    })

    if (subnetOptions?.length === 0 && !includeNoneOption && !includeAutomaticOption) {
      return [{ label: 'No subnets available', value: null }]
    }

    if (includeNoneOption) {
      return [{ label: 'None', value: null }, ...subnetOptions]
    }

    if (includeAutomaticOption) {
      return [{ label: 'Automatic', value: '' }, ...subnetOptions]
    }

    return subnetOptions
  }, [networksById, networkSubnets, includeNoneOption, includeAutomaticOption, includeNetworkName])

  return (
    <AsyncDropdown
      {...rest}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loading}
      items={options}
    />
  )
}
